import Vue from 'vue'
import { launchdarklyKeys } from '@/utils/launchdarklyKeys'
import { evaluateRouteMeta } from '@/utils/routeFeatureFlagMeta'
import {
  buildLaunchDarklyContextFromState,
  getLaunchDarklyContextKey,
} from '@/utils/launchDarklyContext'

const TRACKED_FLAG_KEYS = [...new Set(Object.values(launchdarklyKeys))]

const state = {
  ready: false,
  values: {},
  listenersBound: false,
}

const mutations = {
  setFeatureFlags(s, payload) {
    s.values = { ...payload }
  },
  setFeatureFlagsReady(s, ready) {
    s.ready = ready
  },
  setFeatureFlagsListenersBound(s, bound) {
    s.listenersBound = bound
  },
}

function readAll(client) {
  const next = {}
  for (const key of TRACKED_FLAG_KEYS) {
    next[key] = Boolean(client.variation(key, false))
  }
  return next
}

let syncLaunchDarklyContextInFlight = null
let syncLaunchDarklyContextCoalesced = false

let ensureReadyInFlight = null
let lastIdentifiedContextKey = null

function isLaunchDarklyContextCurrent(rootState, featureFlagsState) {
  return (
    featureFlagsState.ready && getLaunchDarklyContextKey(rootState) === lastIdentifiedContextKey
  )
}

async function runSyncLaunchDarklyContext({ dispatch, rootState, state }) {
  if (isLaunchDarklyContextCurrent(rootState, state)) {
    return
  }

  const ld = Vue.prototype.$launchDarkly
  const contextToUse = buildLaunchDarklyContextFromState(rootState)

  if (!ld.isInitialized()) {
    ld.initialize(contextToUse)
  }
  const client = await ld.getClient()
  await client.identify(contextToUse)

  lastIdentifiedContextKey = getLaunchDarklyContextKey(rootState)
  await dispatch('bindLaunchDarklyClient', client)
}

const actions = {
  bindLaunchDarklyClient({ commit, state }, client) {
    const sync = () => {
      commit('setFeatureFlags', readAll(client))
      commit('setFeatureFlagsReady', true)
    }

    if (!state.listenersBound) {
      commit('setFeatureFlagsListenersBound', true)
      for (const key of TRACKED_FLAG_KEYS) {
        client.on(`change:${key}`, sync)
      }
    }
    sync()
  },

  resetLaunchDarklyContext({ commit }) {
    lastIdentifiedContextKey = null
    commit('setFeatureFlags', {})
    commit('setFeatureFlagsReady', false)
  },

  async syncLaunchDarklyContext(actionContext) {
    if (syncLaunchDarklyContextInFlight) {
      syncLaunchDarklyContextCoalesced = true
      return syncLaunchDarklyContextInFlight
    }

    syncLaunchDarklyContextInFlight = (async () => {
      try {
        do {
          syncLaunchDarklyContextCoalesced = false
          await runSyncLaunchDarklyContext(actionContext)
        } while (syncLaunchDarklyContextCoalesced)
      } finally {
        syncLaunchDarklyContextInFlight = null
      }
    })()

    return syncLaunchDarklyContextInFlight
  },

  async ensureReady(actionContext) {
    if (ensureReadyInFlight) {
      return ensureReadyInFlight
    }

    ensureReadyInFlight = (async () => {
      const { dispatch, state, rootState } = actionContext
      const { auth, charities } = rootState

      try {
        if (auth.accessToken && auth.adminInfo) {
          if (!charities.charities?.length) {
            await dispatch('charities/fetchCharities', null, { root: true })
          }

          const charityId = auth.activeCharityId
          if (charityId && !charities.activeCharityInfo?.charity_id) {
            await dispatch('charities/fetchCharityConfig', { charityId }, { root: true })
          }

          if (!isLaunchDarklyContextCurrent(rootState, state)) {
            await dispatch('syncLaunchDarklyContext')
          }
          return
        }

        if (!state.ready) {
          const ld = Vue.prototype.$launchDarkly
          const client = await ld.getClient()
          await dispatch('bindLaunchDarklyClient', client)
        }
      } finally {
        ensureReadyInFlight = null
      }
    })()

    return ensureReadyInFlight
  },
}

const getters = {
  flagValues: (s) => s.values,
  evaluateRouteRecord: (s) => (record) => evaluateRouteMeta(record.meta, s.values),
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
